import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import InputField from '../../components/InputField/InputField';
import Button from '../../components/Button/Button';
import styles from './ForgetPasswordForm.module.css';
import { apiService } from '../../services/api';
import { normalizeApiError } from '../../types/api-errors';
import { useToast } from '../../contexts/ToastContext';
import { emailSchema } from '../../lib/validation';

interface ForgetPasswordFormProps {
    // AB#1453: 'signup' added to match the updated FormType.
    toggleForm: (form: 'login' | 'forgetPassword' | 'partner' | 'signup') => void;
}

type Step = 'step1' | 'step2';
type ResetMethod = 'email' | 'phone';

const emailResetSchema = z.object({ email: emailSchema });
type EmailResetForm = z.infer<typeof emailResetSchema>;

const phoneResetSchema = z.object({
    phone: z.string().regex(/^\d{7,15}$/, 'Invalid phone number. Please enter 7-15 digits.'),
});
type PhoneResetForm = z.infer<typeof phoneResetSchema>;

const ForgetPasswordForm: React.FC<ForgetPasswordFormProps> = ({ toggleForm }) => {
    const [resetMethod, setResetMethod] = useState<ResetMethod>('email');
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState<Step>('step1');
    const { showToast } = useToast();

    const emailForm = useForm<EmailResetForm>({
        resolver: zodResolver(emailResetSchema),
        defaultValues: { email: '' },
    });

    const phoneForm = useForm<PhoneResetForm>({
        resolver: zodResolver(phoneResetSchema),
        defaultValues: { phone: '' },
    });

    const handleRequestReset = async (identifier: string) => {
        setLoading(true);
        // Security (OWASP OAT-010): Always show the same generic message
        // regardless of whether the account exists. This prevents account
        // enumeration via the Forgot Password flow.
        const genericMessage = resetMethod === 'email'
            ? 'If an account with that email exists, a password reset link has been sent.'
            : 'If an account with that phone number exists, a password reset link has been sent.';
        try {
            await apiService.requestPasswordReset({ identifier });
            showToast(genericMessage, 'success');
            setStep('step2');
        } catch (err: unknown) {
            // AB#1919 (WS-5b) — status + field errors + message via the single
            // normalizer (status/detail prefer 7807, fall back to legacy).
            const appError = normalizeApiError(err);
            const status = appError.status;
            const serverErrors = appError.errors;
            const serverMessage = appError.detail.toLowerCase();
            const firstPwReset = (serverErrors?.PasswordReset?.[0] ?? '').toLowerCase();

            const isUserNotFound =
                status === 404 ||
                firstPwReset.includes('user not found') ||
                firstPwReset.includes('not found') ||
                firstPwReset.includes('no account') ||
                serverMessage.includes('user not found') ||
                serverMessage.includes('not found') ||
                serverMessage.includes('no account');

            if (isUserNotFound) {
                // Mask the "user not found" signal: same success message
                showToast(genericMessage, 'success');
                setStep('step2');
            } else {
                const msg = resetMethod === 'email'
                    ? 'Failed to send email. Please try again later.'
                    : 'Failed to send reset request. Please try again later.';
                showToast(msg, 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const onEmailSubmit = (values: EmailResetForm) => handleRequestReset(values.email);
    const onPhoneSubmit = (values: PhoneResetForm) => handleRequestReset(`+971${values.phone.replace(/\s/g, '')}`);

    if (step === 'step2') {
        const sentViaLabel = resetMethod === 'email' ? 'email' : 'phone number';
        const resendHandler = resetMethod === 'email'
            ? emailForm.handleSubmit(onEmailSubmit)
            : phoneForm.handleSubmit(onPhoneSubmit);
        return (
            <div className={styles.informAndResend}>
                <h2 className={styles.forgetTextH}>
                    {resetMethod === 'email' ? 'Email has been sent' : 'Reset link has been sent'}
                </h2>
                <div>
                    <h3 className={styles.forgetTextP}>Click on the verification link sent to your</h3>
                    <h3 className={styles.forgetTextP}>{sentViaLabel} so we can reset your password</h3>
                </div>
                <div className={styles.resendOption}>
                    <span>Didn't receive it yet?</span>
                    <span className={styles.link}>
                        <Button onClick={resendHandler} label="Resend" noAppearance={true} size="small" />
                    </span>
                </div>
            </div>
        );
    }

    return (
        <form
            className={styles.ForgetPasswordForm}
            onSubmit={resetMethod === 'email'
                ? emailForm.handleSubmit(onEmailSubmit)
                : phoneForm.handleSubmit(onPhoneSubmit)
            }
        >
            <h2 className={styles.forgetTextH}>Let's reset your Password</h2>
            <div>
                <h3 className={styles.forgetTextP}>
                    Enter your {resetMethod === 'email' ? 'email address' : 'phone number'} so we can reset
                </h3>
                <h3 className={styles.forgetTextP}>your password</h3>
            </div>

            <div className={styles.methodSelector}>
                <button
                    type="button"
                    className={`${styles.methodTab} ${resetMethod === 'email' ? styles.active : ''}`}
                    onClick={() => setResetMethod('email')}
                >
                    Email
                </button>
                <button
                    type="button"
                    className={`${styles.methodTab} ${resetMethod === 'phone' ? styles.active : ''}`}
                    onClick={() => setResetMethod('phone')}
                >
                    Phone Number
                </button>
            </div>

            <div className={styles.forgetFields}>
                {resetMethod === 'email' ? (
                    <Controller
                        name="email"
                        control={emailForm.control}
                        render={({ field }) => (
                            <InputField
                                label="Email"
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="yourmail@mail.com"
                                type="email"
                                feedback={emailForm.formState.errors.email ? 'error' : undefined}
                                feedbackMessage={emailForm.formState.errors.email?.message}
                            />
                        )}
                    />
                ) : (
                    <div className={styles.phoneInputWrapper}>
                        <span className={styles.phonePrefix}>+971</span>
                        <Controller
                            name="phone"
                            control={phoneForm.control}
                            render={({ field }) => (
                                <InputField
                                    label="Phone Number"
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="50 123 4567"
                                    type="tel"
                                    feedback={phoneForm.formState.errors.phone ? 'error' : undefined}
                                    feedbackMessage={phoneForm.formState.errors.phone?.message}
                                />
                            )}
                        />
                    </div>
                )}
            </div>

            <div className={styles.buttonWrpr}>
                <Button
                    type="submit"
                    label={loading ? 'Sending...' : 'Submit'}
                    variant="primary"
                    disabled={loading}
                />
            </div>

            <div className={styles.loginOptions} style={{ marginTop: '1rem', textAlign: 'center' }}>
                <span className={styles.link}>
                    <Button
                        label="Back to Login"
                        onClick={() => toggleForm('login')}
                        noAppearance={true}
                        size="small"
                    />
                </span>
            </div>
        </form>
    );
};

export default ForgetPasswordForm;
